import { Controller } from "@hotwired/stimulus"

// ui--floating-menu — a lightweight popover for row-action menus inside tables.
//
// Why not Ui::DropdownMenuComponent / Ui::PopoverComponent? Both position the
// panel with `position: absolute` inside a `relative` parent. Any ancestor
// `overflow: hidden|auto|scroll` clips the panel — which is the default for
// Ui::DataTableComponent's `overflow-x-auto` wrapper, so the menu is cut off
// at the row bottom.
//
// This controller positions the panel with `position: fixed` computed from
// the trigger's getBoundingClientRect() on open, so the panel escapes ANY
// clipping container and renders in the viewport's top layer.
//
// Targets:
//   - trigger: the button that toggles the panel (click).
//   - panel:   the floating panel (starts hidden).
//
// Behaviour: click outside → close; Escape → close; scroll of any parent →
// reposition (matches native <select> UX). Viewport-edge clamps prevent the
// panel from rendering off-screen on narrow viewports. If an ancestor becomes
// the panel's containing block (transform, filter, layout containment), the
// measured offset is cancelled so the panel still opens at its trigger.
export default class extends Controller {
  static targets = ["trigger", "panel"]

  connect() {
    this._boundOutside = this.#handleOutside.bind(this)
    this._boundEscape = this.#handleEscape.bind(this)
    this._boundReposition = this.#reposition.bind(this)

    // A11y baseline: mark the trigger as controlling the panel so screen
    // readers announce the expanded/collapsed state.
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
      if (this.hasPanelTarget && this.panelTarget.id) {
        this.triggerTarget.setAttribute("aria-controls", this.panelTarget.id)
      }
    }
  }

  disconnect() {
    this.#detach()
  }

  toggle() {
    if (this.panelTarget.classList.contains("hidden")) {
      this.show()
    } else {
      this.hide()
    }
  }

  show() {
    this.panelTarget.classList.remove("hidden")
    if (this.hasTriggerTarget)
      this.triggerTarget.setAttribute("aria-expanded", "true")
    this.#reposition()

    // Move focus INTO the panel so keyboard users don't have to tab from
    // the trigger through every ancestor to reach the menu items.
    const firstItem = this.panelTarget.querySelector(
      '[role="menuitem"], a[href], button:not([disabled])',
    )
    if (firstItem) firstItem.focus()

    // Defer listeners so the click that opened us doesn't immediately close.
    setTimeout(() => {
      document.addEventListener("click", this._boundOutside, true)
      document.addEventListener("keydown", this._boundEscape)
      window.addEventListener("scroll", this._boundReposition, true)
      window.addEventListener("resize", this._boundReposition)
    }, 0)
  }

  hide() {
    // Only restore focus to the trigger when focus was inside the panel we're
    // closing — otherwise a click that landed elsewhere on the page would
    // yank focus back to the kebab and break tab flow.
    const focusWasInside = this.panelTarget.contains(document.activeElement)
    this.panelTarget.classList.add("hidden")
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
      if (focusWasInside) this.triggerTarget.focus()
    }
    this.#detach()
  }

  #detach() {
    document.removeEventListener("click", this._boundOutside, true)
    document.removeEventListener("keydown", this._boundEscape)
    window.removeEventListener("scroll", this._boundReposition, true)
    window.removeEventListener("resize", this._boundReposition)
  }

  #reposition() {
    if (!this.hasTriggerTarget || !this.hasPanelTarget) return
    const triggerRect = this.triggerTarget.getBoundingClientRect()
    const panel = this.panelTarget
    panel.style.position = "fixed"

    // Anchor the panel's right edge to the trigger's right edge so it opens
    // toward the row's start (matches the kebab-in-actions-column pattern).
    // Clamp to keep the panel inside the viewport on narrow screens where
    // `innerWidth - triggerRect.right` would push it partially off-screen.
    const right = Math.max(4, window.innerWidth - triggerRect.right)
    panel.style.right = `${right}px`
    panel.style.left = "auto"

    // Flip upward when the panel would overflow the viewport bottom (rows
    // near the last visible row of a long table).
    const panelHeight = panel.offsetHeight || 240
    const spaceBelow = window.innerHeight - triggerRect.bottom
    const top =
      spaceBelow < panelHeight + 8 && triggerRect.top > panelHeight + 8
        ? triggerRect.top - panelHeight - 4
        : triggerRect.bottom + 4
    panel.style.top = `${top}px`

    // `top`/`right` above are viewport coordinates, which only hold while the
    // viewport is the panel's containing block. An ancestor with a transform,
    // filter or layout containment takes that role — including the
    // `container-type: inline-size` wrapper of Ui::DataTableComponent's
    // container mode on older Chromium (Android System WebView 124), which
    // dropped the panel next to another card (CONEK-8530). Measure where it
    // actually landed and cancel the containing block's offset.
    const placed = panel.getBoundingClientRect()
    const dy = placed.top - top
    const dx = placed.right - (window.innerWidth - right)
    if (Math.abs(dy) > 0.5) panel.style.top = `${top - dy}px`
    if (Math.abs(dx) > 0.5) panel.style.right = `${right + dx}px`
  }

  #handleOutside(event) {
    if (!this.element.contains(event.target)) this.hide()
  }

  #handleEscape(event) {
    if (event.key === "Escape") this.hide()
  }
}
